// features/cart/cartSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SubscriptionPeriod } from '../../pages/Subscription/types';
import type { PetLicenseCartItem } from 'src/utils/petLicenseCart';

interface CartItem {
  PermitID: number;
  PermitName: string;
  Period: SubscriptionPeriod;
  Price: number;
}

interface CartState {
  items: CartItem[];
  totalAmount: number;
  petLicenseItems: PetLicenseCartItem[];
}

const initialState: CartState = {
  items: [],
  totalAmount: 0,
  petLicenseItems: [],
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<CartItem>) => {
      // Check if item already exists
      const existingItem = state.items.find(item => item.PermitID === action.payload.PermitID);
      if (!existingItem) {
        state.items.push(action.payload);
        state.totalAmount += action.payload.Price;
      }
    },
    removeFromCart: (state, action: PayloadAction<{ permitId: number }>) => {
      const itemToRemove = state.items.find(item => item.PermitID === action.payload.permitId);
      if (itemToRemove) {
        state.totalAmount -= itemToRemove.Price;
        state.items = state.items.filter(item => item.PermitID !== action.payload.permitId);
      }
    },
    clearCart: (state) => {
      state.items = [];
      state.totalAmount = 0;
    },
    updateCartItemPeriod: (state, action: PayloadAction<{
      permitId: number;
      period: SubscriptionPeriod;
      newPrice: number;
    }>) => {
      const item1 = state.items.find(item => item.PermitID === action.payload.permitId);
      if (item1) {
        state.totalAmount -= item1.Price;
        item1.Period = action.payload.period;
        item1.Price = action.payload.newPrice;
        state.totalAmount += action.payload.newPrice;
      }
    },
    addPetLicenseToCart: (state, action: PayloadAction<PetLicenseCartItem>) => {
      if (!Array.isArray(state.petLicenseItems)) {
        state.petLicenseItems = [];
      }
      const exists = state.petLicenseItems.some(
        (i) => i.ApplicationID === action.payload.ApplicationID,
      );
      if (!exists) {
        state.petLicenseItems.push(action.payload);
      }
    },
    removePetLicenseFromCart: (state, action: PayloadAction<{ applicationId: number }>) => {
      if (!Array.isArray(state.petLicenseItems)) {
        state.petLicenseItems = [];
        return;
      }
      state.petLicenseItems = state.petLicenseItems.filter(
        (i) => i.ApplicationID !== action.payload.applicationId,
      );
    },
    clearPetLicenseFromCart: (state) => {
      state.petLicenseItems = [];
    },
  }
});

// Export actions
export const {
  addToCart,
  removeFromCart,
  clearCart,
  updateCartItemPeriod,
  addPetLicenseToCart,
  removePetLicenseFromCart,
  clearPetLicenseFromCart,
} = cartSlice.actions;

// Export selectors
export const selectCartItems = (state: { cart: CartState }) => state.cart.items;
export const selectCartTotal = (state: { cart: CartState }) => state.cart.totalAmount;
export const selectPetLicenseCartItems = (state: { cart: CartState }) =>
  state.cart?.petLicenseItems ?? [];
export const selectIsInCart = (permitId: number) =>
  (state: { cart: CartState }) => state.cart.items.some(item => item.PermitID === permitId);

export default cartSlice.reducer;